import React from 'react';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import makeStyles from '@material-ui/core/styles/makeStyles';
import FormHelperText from '@material-ui/core/FormHelperText';
import { getIn } from 'formik';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { lighten } from '@material-ui/core/styles';
import { BACKGROUND_WHITE, BLACK_COLOR } from '../../../const/color-const';

const useStyles = makeStyles(theme => ({
  root: props => ({
    margin: theme.spacing(2, 1, 2, 1),
    textAlign: 'left',
    width: props.wide ? 320 : 220,
  }),
  selectField: {
    backgroundColor: theme.palette.type === 'light' ? lighten(BACKGROUND_WHITE, 0.7) : BLACK_COLOR,
    maxHeight: 60,
    width: '100%',
    height: '100%',
  },
}));

const CommonSelectField = (props) => {
  
  const {
    wide,
    options,
    field,
    form,
    label,
    onChange,
    helperText,
    extraHandleChangeAction,
    date,
    setDirty,
    disabled,
    style,
    ...rest
  } = props;
  
  const touched = getIn(form.touched, field.name);
  const error = getIn(form.errors, field.name);
  
  const componentMeta = form.getFieldMeta(field.name);
  const isUpdated = componentMeta.initialValue !== componentMeta.value;
  
  const classes = useStyles({
    wide: wide,
    isUpdated: isUpdated,
  });
  
  function getTextFieldClassName(isUpdated, isDisabled) {
    return classNames({
      isUpdated: isUpdated,
      isDisabled: isDisabled,
    });
  }
  
  React.useEffect(() => {
    setDirty?.(isUpdated);
  }, [isUpdated, setDirty]);
  
  return (
    <FormControl variant="outlined" error={touched && !!error}
                 className={`${classes.root} ${getTextFieldClassName(isUpdated, disabled)} commonSelectField`}>
      
      <InputLabel>
        {label}
      </InputLabel>
      
      <Select
        variant="outlined"
        label={label}
        disabled={disabled}
        style={style}
        className={`${classes.selectField} ${getTextFieldClassName(isUpdated, disabled)}`}
        value={field.value ?? ''}
        onChange={(event) => {
          form.setFieldValue(field.name, event.target.value);
          if (date) {
            const dateRange = options.find(dateOption => dateOption.value === event.target.value)?.option;
            if (dateRange) {
              const fieldMappings = {
                dateFrom: 'dateFrom',
                dateTo: 'dateTo',
                dateTimeFrom: 'dateFrom',
                dateTimeTo: 'dateTo',
              };
        
              for (const [fieldName, dateRangeProp] of Object.entries(fieldMappings)) {
                if (form.values.hasOwnProperty(fieldName)) {
                  form.setFieldValue(fieldName, dateRange[dateRangeProp]);
                }
              }
            }
          }
    
          if (extraHandleChangeAction) {
            extraHandleChangeAction(event.target.value);
          }
        }}
        MenuProps={{
          getContentAnchorEl: null,
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
        }}
      >
        {options && options.map((option, index) => (
          <MenuItem
            key={index}
            value={option.value}>
            {typeof option.label === 'function'
              ? option.label({ ...rest })
              : option.label}
          </MenuItem>
        ))}
      
      </Select>
  
      {touched && error &&
      <FormHelperText error={touched && !!error}>{touched && error}</FormHelperText>
      }
      {!(touched && error) && helperText &&
      <FormHelperText>{helperText}</FormHelperText>
      }

    </FormControl>
  );
};

CommonSelectField.propTypes = {
  wide: PropTypes.bool,
  options: PropTypes.array,
  field: PropTypes.object,
  form: PropTypes.object,
  label: PropTypes.object,
  onChange: PropTypes.func,
  helperText: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  extraHandleChangeAction: PropTypes.func,
  date: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  setDirty: PropTypes.func,
};

export default CommonSelectField;